/**
 * dashboard/classes/$classId/quiz/$activationId/results.tsx — Tutor
 * class results page (Phase G.5).
 *
 * Loads two endpoints in parallel:
 *   - GET /api/quiz/activation/:id         — title + totalQuestions
 *   - GET /api/quiz/activation/:id/results — per-attempt summary
 *
 * Renders a per-student table (ranked by stars desc, then
 * percentage desc) + the class average. We do NOT currently fetch
 * per-question stats (would need a new endpoint); for now the
 * class average is sufficient. The same `quiz:ended` WS event that
 * the live page listens for also lands users here automatically.
 */

import { type ReactNode, useEffect, useState } from 'react';
import { createFileRoute, Link, redirect, useParams } from '@tanstack/react-router';
import { useQuery } from '@tanstack/react-query';
import { PageHeader } from '../../../../../../components/ui/PageHeader.js';
import { StatePanel } from '../../../../../../components/ui/StatePanel.js';
import { freshSessionQuery, classDetailQuery } from '../../../../../../lib/api/queries.js';

export const Route = createFileRoute('/dashboard/classes/$classId/quiz/$activationId/results')({
  beforeLoad: async ({ context, location }) => {
    const session = await context.queryClient.fetchQuery(freshSessionQuery);
    const user = session?.ok ? (session.data?.user ?? null) : null;
    if (!user) {
      throw redirect({ to: '/login', search: { error: undefined, redirect: location.href } });
    }
    if (user.role === 'parent') {
      throw redirect({ to: '/parent' });
    }
    if (user.role !== 'tutor' && user.role !== 'admin') {
      throw redirect({ to: '/' });
    }
    if (user.role === 'tutor' && !user.isApprovedTutor) {
      throw redirect({ to: '/me' });
    }
    return null;
  },
  component: TutorResultsPage,
});

interface ResultRow {
  user_id: string;
  student_name: string;
  total_correct: number;
  total_questions: number;
  stars_earned: number;
  percentage: number;
  status: 'in_progress' | 'completed';
}

interface ActivationSummary {
  quizTitle: string;
  totalQuestions: number;
  status: 'live' | 'ended';
}

function TutorResultsPage(): ReactNode {
  const { classId, activationId } = useParams({ strict: false }) as { classId: string; activationId: string };
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState<string | null>(null);
  const [summary, setSummary] = useState<ActivationSummary | null>(null);
  const [rows, setRows] = useState<ResultRow[]>([]);
  // Resolve the class name for the heading (BUG-UX-01) via the existing
  // cached class detail query; fall back to the raw id while loading.
  const classDetail = useQuery(classDetailQuery(classId));
  const className = classDetail.data?.ok ? (classDetail.data.data.name ?? classId) : classId;

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const [sumR, resR] = await Promise.all([
          fetch(`/api/quiz/activation/${encodeURIComponent(activationId)}`, { credentials: 'include' }),
          fetch(`/api/quiz/activation/${encodeURIComponent(activationId)}/results`, { credentials: 'include' }),
        ]);
        const sumJ = (await sumR.json()) as { ok: boolean; data?: { quizTitle: string; totalQuestions: number; status: 'live' | 'ended' } };
        const resJ = (await resR.json()) as { ok: boolean; data?: ResultRow[]; error?: { message: string } };
        if (cancelled) return;
        if (!sumJ.ok || !sumJ.data) {
          setLoadError('Gagal memuat aktivasi');
          setLoading(false);
          return;
        }
        if (!resJ.ok) {
          setLoadError(resJ.error?.message ?? 'Gagal memuat hasil');
          setLoading(false);
          return;
        }
        setSummary(sumJ.data);
        setRows(resJ.data ?? []);
        setLoading(false);
      } catch (err) {
        if (cancelled) return;
        setLoadError((err as Error).message);
        setLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [activationId]);

  if (loading) {
    return (
      <main className="page-shell">
        <PageHeader eyebrow="Hasil Quiz" title="Memuat…" />
      </main>
    );
  }
  if (loadError || !summary) {
    return (
      <main className="page-shell">
        <PageHeader eyebrow="Hasil Quiz" title="Aktivasi tidak ditemukan" />
        <StatePanel variant="error" title="Gagal memuat" description={loadError ?? 'Tidak ditemukan'} />
      </main>
    );
  }

  const completed = rows.filter((r) => r.status === 'completed');
  const totalStars = completed.reduce((s, r) => s + r.stars_earned, 0);
  const avgPct = completed.length === 0
    ? 0
    : Math.round(completed.reduce((s, r) => s + r.percentage, 0) / completed.length);
  const maxStars = summary.totalQuestions * 5; // hard max is 5⭐ per question

  return (
    <main className="page-shell" data-testid="results-page">
      <PageHeader
        eyebrow={`Hasil Quiz · Kelas ${className}`}
        title={summary.quizTitle}
        description={`${completed.length} siswa selesai · rata-rata ${avgPct}% · total ${totalStars}⭐`}
        action={
          <Link to="/dashboard/classes/$classId/quiz" params={{ classId }} className="button button-secondary">
            Aktifkan Quiz Lain
          </Link>
        }
      />

      <section className="section">
        <h2 className="section-title">Ringkasan kelas</h2>
        <ul className="metric-row" data-testid="metrics">
          <li className="card">
            <p className="section-eyebrow">Siswa selesai</p>
            <p className="metric-value">{completed.length} / {rows.length}</p>
          </li>
          <li className="card">
            <p className="section-eyebrow">Rata-rata</p>
            <p className="metric-value">{avgPct}%</p>
          </li>
          <li className="card">
            <p className="section-eyebrow">Total bintang</p>
            <p className="metric-value">{totalStars} / {completed.length * maxStars}</p>
          </li>
        </ul>
      </section>

      <section className="section">
        <h2 className="section-title">Per siswa</h2>
        {rows.length === 0 ? (
          <StatePanel variant="empty" title="Belum ada siswa yang join" />
        ) : (
          <table className="data-table" data-testid="results-table">
            <thead>
              <tr>
                <th>#</th>
                <th>Nama</th>
                <th>Benar</th>
                <th>Bintang</th>
                <th>Persentase</th>
                <th>Status</th>
              </tr>
            </thead>
            <tbody>
              {rows.map((r, i) => (
                <tr key={r.user_id}>
                  <td>{i + 1}</td>
                  <td>{r.student_name}</td>
                  <td>{r.total_correct} / {r.total_questions}</td>
                  <td>{r.stars_earned}⭐</td>
                  <td>{r.percentage}%</td>
                  <td>
                    <span className={`status-pill status-${r.status}`}>
                      {r.status === 'completed' ? 'Selesai' : 'Berlangsung'}
                    </span>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </section>
    </main>
  );
}
